/**
 * Token design system Leopart.
 * Importa per replicare il linguaggio visivo in altre pagine.
 */
import React from 'react';

// Tutti i colori sono CSS variable refs: rispettano il toggle light/dark
// in tempo reale (vedi `installLeoStyles()` che inietta `--leo-*` come
// alias dei token globali `--c-*`).
export const leoTokens = {
  bg0: 'var(--leo-bg-0)',
  bg1: 'var(--leo-bg-1)',
  bg2: 'var(--leo-bg-2)',
  bg3: 'var(--leo-bg-3)',
  headerBg: 'var(--leo-bg-2)',

  ink: 'var(--leo-ink)',
  inkSoft: 'var(--leo-ink-soft)',
  muted: 'var(--leo-muted)',
  mutedSoft: 'var(--leo-muted-soft)',

  divider: 'var(--leo-divider)',
  dividerStrong: 'var(--leo-divider-strong)',
  rowDivider: 'var(--leo-row-divider)',
  rowStripe: 'var(--leo-row-stripe)',
  summaryBg: 'var(--leo-bg-0)',
  summaryBgEmphasis: 'var(--leo-summary-emph)',

  secondary0: 'var(--c-navy-0)',
  secondary1: 'var(--leo-secondary-1)',
  secondary2: 'var(--c-navy-2)',

  brand: 'var(--leo-brand)',
  brandStrong: 'var(--leo-brand-strong)',
  brandSoft: 'var(--leo-brand-soft)',
  brandBorder: 'var(--leo-brand-strong)',
  brandInk: 'var(--leo-brand-ink)',

  success: '#4dd292',
  successSoft: '#e6f7ee',
  successInk: '#1a6e44',
  warning: '#f5b350',
  warningSoft: '#fde7c8',
  warningInk: '#8a4a10',
  error: '#f05555',
  errorSoft: '#fcd7d4',
  errorInk: '#9a2418',

  rolePorBg: '#cfe1e8',
  rolePorFg: '#03212c',
  roleDifBg: '#e7e7e7',
  roleDifFg: '#4d4d4d',
  roleCenBg: '#e6f7ee',
  roleCenFg: '#1a6e44',
  roleAttBg: '#fff4d6',
  roleAttFg: '#7a5d10',

  fontUi:
    "'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  fontMono:
    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export type RoleCode = 'POR' | 'DIF' | 'CEN' | 'ATT';

export function roleColors(role: RoleCode | string | undefined): {
  bg: string;
  fg: string;
} {
  switch (role) {
    case 'POR':
      return { bg: leoTokens.rolePorBg, fg: leoTokens.rolePorFg };
    case 'CEN':
      return { bg: leoTokens.roleCenBg, fg: leoTokens.roleCenFg };
    case 'ATT':
      return { bg: leoTokens.roleAttBg, fg: leoTokens.roleAttFg };
    case 'DIF':
    default:
      return { bg: leoTokens.roleDifBg, fg: leoTokens.roleDifFg };
  }
}

/** Calcola iniziali "Mario Rossi" → "MR". */
export function getInitials(
  firstName: string | undefined,
  lastName: string | undefined,
): string {
  const f = (firstName ?? '').trim().charAt(0).toUpperCase();
  const l = (lastName ?? '').trim().charAt(0).toUpperCase();
  return `${f}${l}` || '—';
}

/** Estrae la prima role code POR/DIF/CEN/ATT dal campo roles dell'utente. */
export function primaryRole(
  roles: string[] | undefined,
): RoleCode | undefined {
  if (!roles) return undefined;
  const match = roles.find((r) =>
    ['POR', 'DIF', 'CEN', 'ATT'].includes(r.toUpperCase()),
  );
  return match ? (match.toUpperCase() as RoleCode) : undefined;
}

/**
 * Inietta una sola volta stili globali necessari al redesign:
 * - font Satoshi (Fontshare) + JetBrains Mono (Google)
 * - CSS var :root (--leo-*) per i token, usabili da qualunque componente
 * - reset mirato su .leo-scope: font-family + color + box-sizing
 *
 * Chiamalo a inizio pagina (useEffect) nei componenti redesignati.
 */
let _installed = false;
export function installLeoStyles() {
  if (typeof document === 'undefined' || _installed) return;
  _installed = true;

  // Fonts
  if (!document.getElementById('leo-fonts-satoshi')) {
    const l1 = document.createElement('link');
    l1.id = 'leo-fonts-satoshi';
    l1.rel = 'stylesheet';
    l1.href =
      'https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700&display=swap';
    document.head.appendChild(l1);
  }
  if (!document.getElementById('leo-fonts-mono')) {
    const l2 = document.createElement('link');
    l2.id = 'leo-fonts-mono';
    l2.rel = 'stylesheet';
    l2.href =
      'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap';
    document.head.appendChild(l2);
  }

  // Global CSS
  if (!document.getElementById('leo-scope-css')) {
    const s = document.createElement('style');
    s.id = 'leo-scope-css';
    s.textContent = `
:root {
  /* Token Carichi remappati alle CSS variables globali tema (--c-*) →
     la pagina segue automaticamente il toggle light/dark senza riscrittura. */
  --leo-ink: var(--c-text-0);
  --leo-ink-soft: var(--c-text-1);
  --leo-muted: var(--c-text-2);
  --leo-muted-soft: var(--c-text-3);
  --leo-bg-0: var(--c-app-bg);
  --leo-bg-1: var(--c-card-bg);
  --leo-bg-2: var(--c-chip-bg);
  --leo-bg-3: var(--c-icon-wrap-bg);
  --leo-divider: var(--c-card-border);
  --leo-divider-strong: var(--c-card-border-strong);
  --leo-row-divider: var(--c-divider);
  --leo-row-stripe: var(--c-card-bg-elevated);
  --leo-summary-emph: var(--c-card-bg-elevated);
  --leo-brand: var(--c-yellow);
  --leo-brand-strong: var(--c-yellow-deep);
  --leo-brand-soft: var(--c-yellow-soft);
  --leo-brand-ink: var(--c-yellow-on-dark);
  --leo-secondary-1: var(--c-navy-1);
}

.leo-scope, .leo-scope * {
  box-sizing: border-box;
  font-family: ${leoTokens.fontUi};
}
.leo-scope { color: var(--leo-ink); }
.leo-scope .leo-mono { font-family: ${leoTokens.fontMono}; }

/* Table reset dentro scope — Chakra v3 lascia td/th con padding variabile */
.leo-scope table { border-collapse: separate; border-spacing: 0; width: 100%; }
.leo-scope th, .leo-scope td { vertical-align: middle; }

/* Select nativo con stile brand */
.leo-scope select.leo-select {
  padding: 8px 30px 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--leo-divider-strong);
  background: var(--c-input-bg);
  font-size: 14px;
  font-weight: 600;
  color: var(--leo-ink);
  font-family: inherit;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235a5a5a' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

/* Bottoni toolbar redesign */
.leo-scope .leo-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px;
  font-family: inherit; font-size: 12.5px; font-weight: 600;
  color: var(--leo-ink);
  background: var(--c-card-bg);
  border: 1px solid var(--leo-divider-strong);
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms, border-color 120ms;
}
.leo-scope .leo-btn:hover { background: var(--leo-bg-2); border-color: var(--c-text-3); }
.leo-scope .leo-btn--primary {
  background: var(--leo-brand-strong);
  border-color: var(--leo-brand-strong);
  color: var(--leo-brand-ink);
}
.leo-scope .leo-btn--primary:hover {
  background: var(--leo-brand);
  border-color: var(--leo-brand);
}
.leo-scope .leo-btn--ghost { background: transparent; border-color: transparent; color: var(--leo-muted); }
.leo-scope .leo-btn--ghost:hover { color: var(--leo-ink); background: var(--leo-bg-2); }

/* Tab bar (Calendario / Tabella / Report) */
.leo-scope .leo-tabbar { display: flex; gap: 24px; border-bottom: 1px solid var(--leo-divider); padding: 0 28px; }
.leo-scope .leo-tab {
  position: relative;
  padding: 12px 2px;
  font-size: 13.5px; font-weight: 500; color: var(--leo-muted);
  background: none; border: none; cursor: pointer; font-family: inherit;
  display: inline-flex; align-items: center; gap: 7px;
}
.leo-scope .leo-tab.is-active { color: var(--leo-ink); font-weight: 600; }
.leo-scope .leo-tab.is-active::after {
  content: ''; position: absolute; left: 0; right: 0; bottom: -1px;
  height: 2px; background: var(--leo-brand-strong); border-radius: 2px 2px 0 0;
}

/* Segmented (Giorno/Settimana/Aggregato/Per-sessione, Assoluti//min) */
.leo-scope .leo-segmented { display: inline-flex; background: var(--leo-bg-2); padding: 3px; border-radius: 9px; border: 1px solid var(--leo-divider); gap: 2px; }
.leo-scope .leo-seg { padding: 6px 12px; font-size: 12.5px; font-weight: 500; color: var(--leo-muted); background: transparent; border: none; border-radius: 6px; cursor: pointer; font-family: inherit; }
.leo-scope .leo-seg.is-active { background: var(--c-card-bg); color: var(--leo-ink); font-weight: 600; box-shadow: var(--c-card-shadow); }

/* Preset chip */
.leo-scope .leo-chip { padding: 5px 11px; font-size: 12px; font-weight: 500; border-radius: 999px; border: 1px solid var(--leo-divider-strong); background: var(--c-card-bg); color: var(--leo-ink-soft); cursor: pointer; font-family: inherit; white-space: nowrap; }
.leo-scope .leo-chip:hover { background: var(--leo-bg-2); }
.leo-scope .leo-chip.is-active { background: var(--leo-brand); border-color: var(--leo-brand-strong); color: var(--leo-brand-ink); font-weight: 600; }

.leo-scope input.leo-date {
  padding: 7px 10px; font-family: inherit; font-size: 12.5px;
  border: 1px solid var(--leo-divider-strong); border-radius: 8px;
  color: var(--leo-ink); background: var(--c-input-bg);
}
`;
    document.head.appendChild(s);
  }
}

/**
 * Helper per CustomButton — wrappa icon + label in flex inline.
 * Risolve bug "icon e label su righe diverse" causato da Typography block.
 */
export function btnInline(
  icon: React.ReactNode,
  label: React.ReactNode,
): React.ReactElement {
  return React.createElement(
    'span',
    { style: { display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1 } },
    icon,
    label,
  );
}
